import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Building2, Landmark, MonitorSmartphone, Utensils, Users, BellDot, Database,
  Search, SearchX, ChevronRight, ExternalLink, Star, History, X as XIcon,
  FileBadge, Palette, Coins, Wand2, Receipt, Calculator, FileText, ShieldCheck, Lock,
  Printer, CreditCard, Monitor, Tag, AlertCircle, ChefHat, CalendarClock, Truck, Globe,
  Banknote, GitCompareArrows, Landmark as Bank, Mail, MessageCircle, Bell, Camera,
  Upload, Clock, AlertTriangle, Smartphone, Rocket, ServerCog, Building, ListChecks,
} from 'lucide-react';

// Each item links to either an existing ROUTE or switches to an existing in-page TAB.
// `tab` values map to the real <TabsTrigger value> in SettingsPage.
const CATEGORIES = [
  {
    category: 'Company Workspace', icon: Building2, desc: 'Profile, branding & core setup',
    items: [
      { name: 'Company Profile', route: '/settings/company-profile', icon: Building, testid: 'hub-company-profile' },
      { name: 'Branding & PDF', route: '/branding', icon: Palette, testid: 'hub-branding' },
      { name: 'Currency', route: '/currency-settings', icon: Coins, testid: 'hub-currency' },
      { name: 'Company Info', tab: 'company', icon: FileBadge, testid: 'hub-company-info' },
      { name: 'Setup Wizard', route: '/setup-wizard', icon: Wand2, testid: 'hub-setup-wizard' },
    ],
  },
  {
    category: 'Finance & Taxes', icon: Landmark, desc: 'Tax rules, invoicing & banking',
    items: [
      { name: 'Tax Settings', route: '/tax-settings', icon: Calculator, testid: 'hub-tax-settings' },
      { name: 'Tax Submissions', route: '/tax-submissions', icon: FileText, testid: 'hub-tax-submissions', country: ['sa'] },
      { name: 'Monthly Tax Report', route: '/monthly-tax-report', icon: FileText, testid: 'hub-monthly-tax' },
      { name: 'ZATCA e-Invoicing', tab: 'zatca', icon: ShieldCheck, testid: 'hub-zatca', country: ['sa'] },
      { name: 'Period Locks', route: '/settings/period-locks', icon: Lock, testid: 'hub-period-locks' },
      { name: 'Banking Hub', route: '/banking-hub', icon: Bank, testid: 'hub-banking-hub' },
      { name: 'Bank Matching', route: '/bank-matching-settings', icon: GitCompareArrows, testid: 'hub-bank-matching' },
      { name: 'Banking Rules', route: '/banking-rules', icon: ListChecks, testid: 'hub-banking-rules' },
      { name: 'Loans', route: '/loans', icon: Banknote, testid: 'hub-loans' },
    ],
  },
  {
    category: 'POS & Devices', icon: MonitorSmartphone, desc: 'This terminal, printers, payment terminals, devices & receipts',
    items: [
      { name: 'This Terminal & Devices', route: '/settings/pos-devices', icon: Monitor, testid: 'hub-pos-devices' },
      { name: 'Printers', route: '/settings/printers', icon: Printer, testid: 'hub-printers' },
      { name: 'Printer Setup', route: '/printer-setup', icon: Printer, testid: 'hub-printer-setup' },
      { name: 'Payment Terminals', route: '/settings/payment-terminals', icon: CreditCard, testid: 'hub-payment-terminals' },
      { name: 'Receipts & Go Green', route: '/settings/receipt', icon: Receipt, testid: 'hub-receipt' },
      { name: 'Open POS (Kiosk)', route: '/cashier/pos?kiosk=1', icon: Rocket, testid: 'open-pos-kiosk-btn' },
      { name: 'Geidea', route: '/settings/geidea', icon: CreditCard, testid: 'hub-geidea' },
      { name: 'Customer Display Promos', route: '/customer-display/promos', icon: Monitor, testid: 'hub-customer-display' },
    ],
  },
  {
    category: 'Operations & Menu', icon: Utensils, desc: 'Tables, kitchen flows & ordering',
    items: [
      { name: 'Upsells', route: '/upsells', icon: Rocket, testid: 'hub-upsells' },
      { name: 'Table Management', route: '/table-management', icon: Utensils, testid: 'hub-table-management' },
      { name: 'Allergens', route: '/allergens', icon: AlertCircle, testid: 'hub-allergens' },
      { name: 'Timed Events', route: '/timed-events', icon: CalendarClock, testid: 'hub-timed-events' },
      { name: 'Reservations', route: '/reservations', icon: CalendarClock, testid: 'hub-reservations' },
    ],
  },
  {
    category: 'Team & Access', icon: Users, desc: 'Roles, permissions & attendance',
    items: [
      { name: 'Roles & Permissions', route: '/role-management', icon: Users, testid: 'hub-role-management' },
      { name: 'Access Control', tab: 'access', icon: Lock, testid: 'hub-access-control' },
      { name: 'Attendance Config', tab: 'attendance-config', icon: Clock, testid: 'hub-attendance-config' },
    ],
  },
  {
    category: 'Integrations & Alerts', icon: BellDot, desc: 'Email, WhatsApp, alerts & CCTV',
    items: [
      { name: 'Email / SMTP', tab: 'email', icon: Mail, testid: 'hub-email' },
      { name: 'WhatsApp', tab: 'whatsapp', icon: MessageCircle, testid: 'hub-whatsapp' },
      { name: 'WhatsApp Digests', route: '/settings/whatsapp-digests', icon: MessageCircle, testid: 'hub-whatsapp-digests' },
      { name: 'Notifications & Alerts', tab: 'notifications', icon: Bell, testid: 'hub-notifications' },
      { name: 'CCTV', tab: 'cctv', icon: Camera, testid: 'hub-cctv' },
    ],
  },
  {
    category: 'Data & Maintenance', icon: Database, desc: 'Imports, backups & system',
    items: [
      { name: 'Import Data', tab: 'import', icon: Upload, testid: 'hub-import' },
      { name: 'Backup', tab: 'backup', icon: Database, testid: 'hub-backup' },
      { name: 'Scheduler', tab: 'scheduler', icon: Clock, testid: 'hub-scheduler' },
      { name: 'Data Management', route: '/data-management', icon: Database, testid: 'hub-data-management' },
      { name: 'Data Hygiene', tab: 'hygiene', icon: ShieldCheck, testid: 'hub-hygiene' },
      { name: 'Maintenance', tab: 'maintenance', icon: AlertTriangle, testid: 'hub-maintenance' },
      { name: 'App Updates', tab: 'app-updates', icon: Smartphone, testid: 'hub-app-updates' },
      { name: 'Deploy', tab: 'deploy', icon: ServerCog, testid: 'hub-deploy' },
    ],
  },
];

// Flat lookup by stable id (= testid) for the Favorites / Recently-used rows.
const ITEM_BY_ID = {};
CATEGORIES.forEach((c) => c.items.forEach((i) => { ITEM_BY_ID[i.testid] = { ...i, category: c.category }; }));

const FAV_KEY = 'settings-favorites';
const RECENT_KEY = 'settings-recents';

export function SettingsHub({ onOpenTab }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [recents, setRecents] = useState([]);
  // iter876 — Branch-country for hiding Saudi-specific tabs on UAE/India branches.
  const userBranchId = useAuthStore(s => s.user?.branch_id) || null;
  const { taxConfig } = useTaxConfig(userBranchId);
  const branchCountry = String(taxConfig?.country_code || '').toLowerCase();

  useEffect(() => {
    (async () => {
      try {
        const [f, r] = await Promise.all([
          api.get(`/user-preferences/${FAV_KEY}`),
          api.get(`/user-preferences/${RECENT_KEY}`),
        ]);
        setFavorites(Array.isArray(f.data?.value) ? f.data.value : []);
        setRecents(Array.isArray(r.data?.value) ? r.data.value : []);
      } catch { /* prefs are best-effort */ }
    })();
  }, []);

  const persist = useCallback((key, value) => {
    api.put(`/user-preferences/${key}`, { value }).catch(() => {});
  }, []);

  const toggleFav = useCallback((id, e) => {
    e?.stopPropagation();
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-12);
      persist(FAV_KEY, next);
      return next;
    });
  }, [persist]);

  const recordRecent = useCallback((id) => {
    setRecents((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, 6);
      persist(RECENT_KEY, next);
      return next;
    });
  }, [persist]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    // iter876 — Country-aware hide: items with a `country` array are only
    // shown if the current user's branch (or tenant default) matches.
    // Example: ZATCA e-Invoicing hidden for UAE / India branches.
    const matchesCountry = (item) => {
      if (!Array.isArray(item.country) || item.country.length === 0) return true;
      if (!branchCountry) return true; // unknown → don't hide
      return item.country.map((c) => String(c).toLowerCase()).includes(branchCountry);
    };
    const applyCountry = (cats) => cats
      .map((c) => ({ ...c, items: c.items.filter(matchesCountry) }))
      .filter((c) => c.items.length > 0);
    if (!term) return applyCountry(CATEGORIES);
    return applyCountry(CATEGORIES)
      .map(c => ({ ...c, items: c.items.filter(i => i.name.toLowerCase().includes(term)) }))
      .filter(c => c.items.length > 0);
  }, [q, branchCountry]);

  const open = useCallback((item) => {
    recordRecent(item.testid);
    if (item.route) navigate(item.route);
    else if (item.tab) onOpenTab?.(item.tab);
  }, [navigate, onOpenTab, recordRecent]);

  const searching = q.trim().length > 0;
  const favItems = favorites.map((id) => ITEM_BY_ID[id]).filter(Boolean);
  const recentItems = recents.map((id) => ITEM_BY_ID[id]).filter(Boolean);

  const Chip = ({ item, showUnpin }) => {
    const ItemIcon = item.icon;
    return (
      <span className="group/chip inline-flex items-center gap-1.5 rounded-full border border-border bg-white pl-3 pr-2 py-1.5 text-[12.5px] text-foreground hover:bg-muted transition-colors">
        <button onClick={() => open(item)} className="inline-flex items-center gap-1.5" data-testid={`chip-${item.testid}`}>
          <ItemIcon className="w-3.5 h-3.5 text-muted-foreground" />{item.name}
        </button>
        {showUnpin
          ? <button onClick={(e) => toggleFav(item.testid, e)} className="text-muted-foreground/50 hover:text-rose-500" aria-label="Unpin" data-testid={`unpin-${item.testid}`}><XIcon className="w-3 h-3" /></button>
          : <span className="w-1" />}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="settings-hub">
      <div className="relative w-full md:w-96">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search settings…"
          className="pl-10 h-10 bg-white"
          data-testid="settings-hub-search"
        />
      </div>

      {!searching && favItems.length > 0 && (
        <div data-testid="settings-hub-favorites">
          <div className="flex items-center gap-2 mb-2.5">
            <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
            <h3 className="text-[13px] font-semibold text-foreground font-outfit">Favorites</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {favItems.map((item) => <Chip key={item.testid} item={item} showUnpin />)}
          </div>
        </div>
      )}

      {!searching && recentItems.length > 0 && (
        <div data-testid="settings-hub-recents">
          <div className="flex items-center gap-2 mb-2.5">
            <History className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-[13px] font-semibold text-foreground font-outfit">Recently used</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentItems.map((item) => <Chip key={item.testid} item={item} />)}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="settings-hub-empty">
          <SearchX className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">No settings found</p>
          <p className="text-xs text-muted-foreground mt-1">Try a different search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {filtered.map((cat) => {
            const CatIcon = cat.icon;
            return (
              <Card key={cat.category} className="overflow-hidden border-border shadow-sm" data-testid={`settings-hub-category-${cat.category.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="p-4 border-b border-border flex items-center gap-3 bg-white">
                  <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                    <CatIcon className="w-[18px] h-[18px] text-foreground" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-foreground font-outfit leading-tight">{cat.category}</h3>
                    <p className="text-[11px] text-muted-foreground">{cat.desc}</p>
                  </div>
                </div>
                <div className="p-2 flex flex-col gap-0.5 bg-muted/20">
                  {cat.items.map((item) => {
                    const ItemIcon = item.icon;
                    const isFav = favorites.includes(item.testid);
                    return (
                      <div key={item.name} className="group flex items-center rounded-lg hover:bg-muted transition-colors">
                        <button
                          onClick={() => open(item)}
                          className="flex-1 flex items-center justify-between px-3 py-2.5 text-[13px] text-foreground text-left active:scale-[0.99]"
                          data-testid={item.testid}>
                          <span className="flex items-center gap-2.5">
                            <ItemIcon className="w-4 h-4 text-muted-foreground" />
                            {item.name}
                          </span>
                          {item.route
                            ? <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60" />
                            : <ChevronRight className="w-4 h-4 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </button>
                        <button
                          onClick={(e) => toggleFav(item.testid, e)}
                          className="px-2.5 self-stretch flex items-center text-muted-foreground/40 hover:text-amber-500 transition-colors"
                          aria-label={isFav ? 'Unpin' : 'Pin to favorites'}
                          data-testid={`fav-${item.testid}`}>
                          <Star className={`w-3.5 h-3.5 ${isFav ? 'text-amber-500 fill-amber-400' : ''}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
